import React, { useMemo } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import ExtensionFlagImage from '@common-components/extension-flag-image/extension-flag-image';
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

interface PhoneDisplayWithFlagProps {
    phone: string;
    className?: string;
    flagSize?: string;
    showFlag?: boolean;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export const PhoneDisplayWithFlag: React.FC<PhoneDisplayWithFlagProps> = ({ 
    phone, 
    className, 
    flagSize = '16px',
    showFlag = true 
}) => {
    const { formattedPhone, countryCode } = useMemo(() => {
        if (!phone) return { formattedPhone: '', countryCode: null };

        try {
            // Ensure the phone starts with +
            const phoneWithPlus = phone.startsWith('+') ? phone : `+${phone}`;
            const parsedNumber = parsePhoneNumber(phoneWithPlus);

            const formatted = parsedNumber.formatInternational();
            const regionCode = parsedNumber.country;

            return {
                formattedPhone: formatted,
                countryCode: regionCode?.toLowerCase() || null
            };
        } catch (error) {
            console.warn('[PhoneDisplayWithFlag] Error parsing phone number:', phone, error);
            return { formattedPhone: phone, countryCode: null };
        }
    }, [phone]);

    const countryName = useMemo(() => {
        if (!countryCode) return '';
        try {
            return regionNames.of(countryCode.toUpperCase()) || '';
        } catch (e) {
            return '';
        }
    }, [countryCode]);

    if (!phone) return null;

    return (
        <div className={cn("flex items-center gap-1.5", className)}>
            {showFlag && countryCode && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div style={{ width: flagSize, height: flagSize, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'help' }}>
                            <ExtensionFlagImage 
                                iso2={countryCode} 
                                size={flagSize} 
                                style={{ width: flagSize, height: flagSize, display: 'block', objectFit: 'contain' }} 
                            />
                        </div>
                    </TooltipTrigger>
                    {countryName && (
                        <TooltipContent>
                            <p>{countryName}</p>
                        </TooltipContent>
                    )}
                </Tooltip>
            )}
            <span className="truncate">
                {formattedPhone}
            </span>
        </div>
    );
};

export default PhoneDisplayWithFlag;
